import { DEFAULT_SYNTHESIS_REQUEST } from '@src/constants/voice';
import { isStatusRegression } from '@src/utils/requestStatus';
import { actionTypes } from './actions';

const OUTCOME_FIELDS = [
  'status',
  'progress',
  'audioLink',
  'processingAt',
  'endedAt',
];

export const initialState = {
  synthesisRequest: DEFAULT_SYNTHESIS_REQUEST,
  loadingSynthesisConfig: false,
  loadingLoadRequest: false,
  paragraphs: [],
  isLoadingParagraphs: false,
  selectedParagraph: [],
  previewParagraphs: [],
  sentences: [],
  selectedSentences: [], // list of id sentences selected by user
  selectedSentence: {},
  openSentences: false,
  ttsAttributeSentence: {},
  advanceFeature: false,
  blackWordsInText: [],
  defaultSample: {},
  showSample: true,
  voice: {},
  search: '',
  replace: '',
  relateDataRequest: {},
  showBannerProjects: true,
  ttsPreset: null,
  synthesizingVoice: {},
};

const synthesisRequestReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.UPDATE_SYNTHESIS_REQUEST: {
      const { name, value } = action;
      return {
        ...state,
        synthesisRequest: { ...state.synthesisRequest, [name]: value },
      };
    }

    case actionTypes.UPDATE_SYNTHESIS_CONFIG: {
      const { synthesisRequest: requestUpdate, shallow } = action;
      const prevRequest = state.synthesisRequest;

      // The same request cannot go back to converting once it has finished, so
      // an update saying otherwise carries a stale outcome. Everything else in
      // the payload still applies.
      const keepOutcome =
        Boolean(prevRequest?.id) &&
        requestUpdate?.id === prevRequest.id &&
        isStatusRegression(prevRequest.status, requestUpdate?.status);

      const outcome = keepOutcome
        ? {
            status: prevRequest.status,
            progress: prevRequest.progress,
            audioLink: prevRequest.audioLink,
            endedAt: prevRequest.endedAt,
            processingAt:
              prevRequest.processingAt || requestUpdate?.processingAt,
          }
        : null;

      if (shallow) {
        return {
          ...state,
          synthesisRequest: {
            ...requestUpdate,
            audioType: prevRequest?.audioType,
            backgroundMusic: prevRequest?.backgroundMusic,
            bitrate: prevRequest?.bitrate,
            volume: prevRequest?.volume,
            breakTime: prevRequest?.breakTime,
            voice: requestUpdate?.voice || prevRequest?.voice,
            speed: prevRequest?.speed,
            hasEmphasis: prevRequest?.hasEmphasis,
            emphasis: prevRequest?.emphasis,
            ...outcome,
          },
        };
      }

      if (outcome) {
        return {
          ...state,
          synthesisRequest: { ...requestUpdate, ...outcome },
        };
      }

      return { ...state, synthesisRequest: requestUpdate };
    }

    case actionTypes.APPLY_SYNTHESIS_OUTCOME: {
      const { requestId, status, progress, audioLink, processingAt, endedAt } =
        action;
      const prevRequest = state.synthesisRequest;

      // Only the store knows which request the editor holds right now. A
      // caller's ref or closure can be a commit behind, so the match belongs
      // here rather than at the call site.
      if (!requestId || !prevRequest?.id || prevRequest.id !== requestId)
        return state;

      // A late IN_PROGRESS message must not undo a finished request. Keep the
      // outcome and only fill in a timestamp the finished record is missing.
      if (isStatusRegression(prevRequest.status, status)) {
        if (prevRequest.processingAt || !processingAt) return state;
        return {
          ...state,
          synthesisRequest: { ...prevRequest, processingAt },
        };
      }

      // `undefined` means the message did not carry the field, so the previous
      // value stands. `null` is an explicit clear and is written through.
      const keepPrevious = (nextValue, prevValue) =>
        nextValue === undefined ? prevValue : nextValue;

      const nextRequest = {
        ...prevRequest,
        status: keepPrevious(status, prevRequest.status),
        progress: keepPrevious(progress, prevRequest.progress),
        audioLink: keepPrevious(audioLink, prevRequest.audioLink),
        processingAt: processingAt || prevRequest.processingAt,
        endedAt: endedAt || prevRequest.endedAt,
      };

      // The same outcome is replayed on every poll tick, so one that says
      // nothing new must leave state identity alone and not re-render.
      const isUnchanged = OUTCOME_FIELDS.every(
        (field) => nextRequest[field] === prevRequest[field],
      );
      if (isUnchanged) return state;

      return { ...state, synthesisRequest: nextRequest };
    }

    case actionTypes.UPDATE_LOADING_SYNTHESIS_CONFIG: {
      const { loading } = action;
      return { ...state, loadingSynthesisConfig: loading };
    }

    case actionTypes.UPDATE_BACKGROUND_MUSIC: {
      const { name, link } = action;
      const backgroundMusic = {
        ...state.synthesisRequest.backgroundMusic,
        name,
        link,
      };

      return {
        ...state,
        synthesisRequest: {
          ...state.synthesisRequest,
          backgroundMusic,
        },
      };
    }

    case actionTypes.UPDATE_VOLUME_BACKGROUND_MUSIC: {
      const { volume } = action;
      const backgroundMusic = {
        ...state.synthesisRequest.backgroundMusic,
        volume,
      };

      return {
        ...state,
        synthesisRequest: {
          ...state.synthesisRequest,
          backgroundMusic,
        },
      };
    }

    case actionTypes.UPDATE_PARAGRAPHS: {
      const { paragraphs } = action;
      return { ...state, paragraphs };
    }

    case actionTypes.UPDATE_LOADING_PARAGRAPHS: {
      const { loading } = action;
      return { ...state, isLoadingParagraphs: loading };
    }

    case actionTypes.UPDATE_SELECTED_PARAGRAPH: {
      const { data } = action;
      return { ...state, selectedParagraph: data };
    }

    case actionTypes.UPDATE_PREVIEW_PARAGRAPHS: {
      const { data } = action;
      return { ...state, previewParagraphs: data };
    }

    case actionTypes.UPDATE_SENTENCES: {
      const { sentences } = action;
      return { ...state, sentences };
    }

    case actionTypes.UPDATE_BLACK_WORDS: {
      const { blackWordsInText } = action;
      return { ...state, blackWordsInText };
    }

    case actionTypes.UPDATE_ELEMENT_OF_SENTENCES: {
      const { elements, sentenceId, text, onLoad, mousePointerPosition } =
        action;
      const newSentences = [...state.sentences];

      const index = newSentences.findIndex((item) => item.id === sentenceId);
      if (index === -1) return state;
      newSentences[index] = {
        ...newSentences[index],
        elements,
        text,
        onLoad,
        mousePointerPosition,
      };
      return { ...state, sentences: newSentences };
    }

    case actionTypes.UPDATE_SELECTED_SENTENCE: {
      const { selectedText, sentenceId, startOffset, endOffset } = action;
      const newSelectedSentence = { focusPosition: startOffset, sentenceId };

      if (
        startOffset !== undefined &&
        endOffset !== undefined &&
        startOffset !== endOffset
      ) {
        return {
          ...state,
          selectedSentence: {
            ...newSelectedSentence,
            selectedText,
            startOffset,
            endOffset,
          },
        };
      }
      return { ...state, selectedSentence: newSelectedSentence };
    }

    case actionTypes.UPDATE_OPEN_SENTENCE: {
      const { openSentences } = action;
      return { ...state, openSentences };
    }

    case actionTypes.REMOVE_TTS_ATTRIBUTE_BY_SENTENCE: {
      const { data } = action;
      return { ...state, ttsAttributeSentence: data };
    }

    case actionTypes.SWITCH_ADVANCE_FEATURE: {
      const { advanceFeature } = action;
      return { ...state, advanceFeature };
    }

    case actionTypes.UPDATE_VOICE: {
      const { voice } = action;
      return { ...state, voice };
    }

    case actionTypes.UPDATE_SELECTED_SENTENCES: {
      const { selectedSentences } = action;
      return { ...state, selectedSentences };
    }

    case actionTypes.CHANGE_SEARCH_CONTENT: {
      const { search } = action;
      return { ...state, search };
    }

    case actionTypes.REPLACE_IN_SENTENCES: {
      const { replace } = action;
      return { ...state, replace };
    }

    case actionTypes.CLONE_REQUEST: {
      const { synthesisRequest } = state;
      const { id, status, createdAt, endedAt, progress, title, ...rest } =
        synthesisRequest;
      return {
        ...state,
        synthesisRequest: { ...rest, title: '' },
      };
    }

    case actionTypes.UPDATE_DEFAULT_REQUEST: {
      const { defaultSample } = action;
      return { ...state, defaultSample };
    }

    case actionTypes.UPDATE_SHOW_SAMPLE_SCRIPTS: {
      const { showSample } = action;
      return { ...state, showSample };
    }

    case actionTypes.UPDATE_LOADING_LOAD_REQUEST: {
      const { isLoadingLoadRequest } = action;
      return { ...state, loadingLoadRequest: isLoadingLoadRequest };
    }

    case actionTypes.UPDATE_SHOW_BANNER_PROJECTS: {
      const { showBanner } = action;
      return { ...state, showBannerProjects: showBanner };
    }

    case actionTypes.UPDATE_DATA_RELATED_REQUEST: {
      const { relateDataRequest } = action;
      return { ...state, relateDataRequest };
    }

    case actionTypes.UPDATE_CURRENT_TTS_PRESET: {
      const { ttsPreset } = action;
      return {
        ...state,
        ttsPreset,
      };
    }

    case actionTypes.SET_SYNTHESIZING_VOICE: {
      const { requestId, candidates } = action;
      return {
        ...state,
        synthesizingVoice: {
          ...state.synthesizingVoice,
          [requestId]: candidates,
        },
      };
    }

    case actionTypes.CLEAR_SYNTHESIZING_VOICE: {
      const { requestId } = action;
      const { [requestId]: removed, ...rest } = state.synthesizingVoice;
      return {
        ...state,
        synthesizingVoice: rest,
      };
    }

    default:
      return state;
  }
};

export default synthesisRequestReducer;
